import React, { useEffect, useContext, useState } from 'react';
import dynamic from 'next/dynamic';

import imageUrlBuilder from '@sanity/image-url';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@tmap/mmm-style-guide/src/Button';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { toKey } from '@tmap/mmm-core/entityKey';

import GalleryCommunityCard from '../../components/communityCard/gallery';
import FilterBar, { ResetFiltersContext } from '../../components/filterBar';
import Metatags from '../../components/metatags';
import Pager from '../../components/pager';

import { getCommunities } from '../../actions/community';
import filterCommunities from '../../actions/community/filterCommunities';
import client from '../../client';
import ColorBlocks from '../../layouts/colorBlocks';
import { pickPlural } from '../../lib/helpers';
import { communityFilters, communitySorts } from '../../lib/filterTypes';
import { wrapper } from '../../store';
import setCacheHeaders from '../../lib/setCacheHeaders';

const CardAd = dynamic(import('../../components/cardAd'));

const builder = imageUrlBuilder(client);

const CenteredGrid = styled(Grid)(() => ({
  maxWidth: '600px',
  textAlign: 'center',
}));

const FullWidthPicture = styled('picture')(() => ({
  width: '100%',
}));

const FullWidthImg = styled('img')(() => ({
  width: '100%',
}));

function EmptyCommunityResults(props) {
  const [, setResetFlag] = useContext(ResetFiltersContext);
  const handleResetFilters = () => {
    setResetFlag(true);
  };

  const { image, leadCopy, subLeadCopy } = props;
  return (
    <Grid container direction='column' alignItems='center' spacing={3}>
      <CenteredGrid item>
        <FullWidthPicture>
          <source srcSet={builder.image(image).format('webp').url()} />
          <FullWidthImg src={builder.image(image).url()} alt={image.alt} />
        </FullWidthPicture>
      </CenteredGrid>
      <Grid item xs={12}><Typography variant='h3'>{leadCopy}</Typography></Grid>
      <Grid item xs={12}><Typography variant='body1' color='textSecondary'>{subLeadCopy}</Typography></Grid>
      <Grid item xs={12}>
        <Button
          variant='contained'
          color='primary'
          onClick={handleResetFilters}
        >
          Clear All Filters
        </Button>
      </Grid>
    </Grid>
  );
}

const COMMUNITIES_PER_PAGE = 18;

function MovingLiving(props) {
  const { doc } = props;

  const communities = useSelector((state) => state?.communities?.communities);
  const filter = useSelector((state) => state?.communities?.filter);

  const dispatch = useDispatch();
  const router = useRouter();
  const { page = '1' } = router.query;

  useEffect(() => {
    if (communities && filter?.results) {
      const missingCommunities = filter.results.filter((key) => !(key in communities));
      if (missingCommunities.length !== 0) {
        dispatch(getCommunities(missingCommunities.map((id) => toKey('community', id))));
      }
    }
  }, [communities, filter, dispatch]);

  const resetFiltersContextValue = useState(false);

  return (
    <>
      <Metatags
        title={doc.communityPageTitle || 'Communities'}
        canonical={`${process.env.NEXT_PUBLIC_CANONICAL_DOMAIN}/moving-living`}
        description={doc.communityPageMetaDescription || ''}
        image={doc.heroImage && builder.image(doc.heroImage).url()}
      />
      <ResetFiltersContext.Provider value={resetFiltersContextValue}>
        <ColorBlocks headerElevation={2}>
          <ColorBlocks.Block>
            <Box display='flex' flexDirection='column' alignItems='center' textAlign='center'>
              <Typography
                component='h1'
                sx={{ fontSize: { xs: '32px', md: '42px' } }}
                fontWeight={800}
                lineHeight='36px'
              >
                {doc.communityPageTitle}
              </Typography>
              <Typography
                component='h2'
                variant='body1'
                color='textSecondary'
                sx={{ fontSize: { xs: '16px', md: '20px' }, maxWidth: '650px' }}
                lineHeight='24px'
                letterSpacing='0.15px'
                marginTop={2}
              >
                {doc.communityPageMetaDescription}
              </Typography>
            </Box>
          </ColorBlocks.Block>
          <ColorBlocks.Block>
            <FilterBar
              total={filter.total}
              query={filter.query}
              entityName={pickPlural(filter.total, 'Community', 'Communities')}
              sorts={communitySorts}
              filters={communityFilters}
            />
            <Grid container rowSpacing={{ xs: 4, md: 6 }} columnSpacing={2} justifyContent='center'>
              {filter.total === 0 ? (
                <Grid item xs={12}><EmptyCommunityResults {...doc.emptyState} /></Grid>
              ) : (
                <>
                  {(filter.results || []).map((id, i) => {
                    const community = communities[id];
                    if (!community) {
                      return null;
                    }
                    return (
                      <Grid item xs={12} sm={6} lg={4} key={community._id}>
                        <GalleryCommunityCard
                          community={community}
                          shouldLazyLoad={i > 3}
                          lazyProps={{ offset: 500 }}
                          preload={i === 0}
                          placementId={`exploreCommunities-p${page}-${i}`}
                          pageId='exploreCommunities'
                          showProgramValue={filter.query.relocation === 'true'}
                        />
                      </Grid>
                    );
                  })}
                  <Grid item xs={12}>
                    <Pager maxPage={Math.ceil(filter.total / COMMUNITIES_PER_PAGE)} />
                  </Grid>
                </>
              )}
            </Grid>
          </ColorBlocks.Block>
          <ColorBlocks.Interstitial colorTop='light' colorBottom='light'>
            <CardAd leadCopy={doc.notificationBlockLead} />
          </ColorBlocks.Interstitial>
        </ColorBlocks>
      </ResetFiltersContext.Provider>
    </>
  );
}

export const getServerSideProps = wrapper.getServerSideProps(
  (store) => async ({ query, res }) => {
    const [result] = await Promise.all([
      client.fetch('*[_id == "communitySettings"][0]'),
      store.dispatch(filterCommunities(query, COMMUNITIES_PER_PAGE)),
    ]);
    setCacheHeaders(res, 300, 3600);
    return { props: { doc: result } };
  },
);

export default MovingLiving;
